import type { TextBlock } from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import TTHeading, { type Level } from "@tiptap/extension-heading"
import type { Node as ProsemirrorNode, Schema } from "prosemirror-model"

import { Text } from "../../coreExtensions"
import { converter as TextDirection } from "../../globalExtensions/TextDirection"
import { HeadingTextExtension } from "../../models"

const name = "heading"

interface HeadingName {
  fullName: RichTextNodeType
  level: Level
}

function levelToNodeType(level: Level): RichTextNodeType {
  switch (level) {
    case 1:
      return RichTextNodeType.heading1
    case 2:
      return RichTextNodeType.heading2
    case 3:
      return RichTextNodeType.heading3
    case 4:
      return RichTextNodeType.heading4
    case 5:
      return RichTextNodeType.heading5
    case 6:
      return RichTextNodeType.heading6
  }
}

function getHeadingName(level: Level): HeadingName {
  return {
    fullName: levelToNodeType(level),
    level
  }
}

const defaultLevelOptions: Level[] = [1, 2, 3, 4, 5, 6]

function fromPrismic(
  headingName: string,
  levels: Level[] = defaultLevelOptions
): HeadingName | undefined {
  const nameReg = new RegExp(`^heading([${levels.join(",")}]){1}$`)

  const matched = headingName.match(nameReg)

  if (!matched || !matched[1]) return

  const level = parseInt(matched[1]) as Level

  return {
    fullName: levelToNodeType(level),
    level
  }
}

function fromProsemirror(
  nodeAttributes: ProsemirrorNode["attrs"],
  levels: Level[]
): HeadingName | undefined {
  const level = nodeAttributes["level"] as Level

  if (!levels.includes(level)) return

  return {
    fullName: levelToNodeType(level),
    level
  }
}

const converter = (levels: Level[]) => {
  const defaultLevel = levels[0] ?? 1

  return {
    fromPrismic(schema: Schema) {
      return function (heading: TextBlock): ProsemirrorNode | undefined {
        const content = Text.converter.fromPrismic(schema)(name, heading)

        const headingName =
          fromPrismic(heading.type, levels) || getHeadingName(defaultLevel) //used a fallback if content is heading but heading level is not available anymore

        return schema.nodes[name]?.create(
          { level: headingName.level, ...TextDirection.fromPrismic(heading) },
          content
        )
      }
    },

    toPrismic(node: ProsemirrorNode): TextBlock | undefined {
      const { fullName: nodeType } =
        fromProsemirror(node.attrs, levels) || getHeadingName(defaultLevel)
      return Text.converter.toPrismic(
        node,
        nodeType,
        TextDirection.toPrismic(node)
      )
    }
  }
}

function headingLevels(nodeTypes?: Set<RichTextNodeType>): Level[] {
  if (!nodeTypes) return defaultLevelOptions

  if (!nodeTypes.size) return []

  const levels = Array.from(nodeTypes.values())
    .reduce<Level[]>((acc, nodeType) => {
      const matchedHeading = fromPrismic(nodeType)

      if (!matchedHeading) return acc

      return [...acc, matchedHeading.level]
    }, [])
    .sort((a, b) => a - b)

  return levels
}

function createComponent(levels: Level[]) {
  return TTHeading.extend({
    name: "heading"
  }).configure({ levels })
}

// eslint-disable-next-line import/no-default-export
export default {
  name,
  build: (nodeTypes?: Set<RichTextNodeType>) => {
    const levels = headingLevels(nodeTypes)

    if (!levels) return

    const headingNodes = levels.map((level) => levelToNodeType(level))

    return new HeadingTextExtension(
      headingNodes,
      createComponent(levels),
      levels,
      converter(levels),
      (nodeAttrs: ProsemirrorNode["attrs"]) => {
        const headingName = fromProsemirror(nodeAttrs, levels)
        return headingName && `Heading ${headingName.level}`
      }
    )
  }
}

export type { Level }
